// Turbine serials (enable_turbine_serial sites) — frontend half of the C++
// get_enable_turbine_serial / get_ui_turbine_serial_model pair used by the
// event-time lists (load_data_shell, report_time_shell, graph_channel_selection).
//
// On a serial-tracking site an event's row shows the serial of the engine that
// was on the stand on that day. The backend learns the serial list from the
// EVENT_MODEL_INFO reply (event_model_time::cache_reply_turbine_serials), so the
// list is (re)fetched *after* an event list arrives — pass the event array as the
// hook's refresh key.
import { useCallback, useEffect, useState } from "react";
import { tigerSecondsToDate } from "./tigerTime";

export interface TurbineSerialEntry {
  day: number;
  month: number;
  year: number;
  serial: string;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

let enabledPromise: Promise<boolean> | null = null;

/** Whether the site tracks turbine serials (site tiger.ini
 *  `[Turbine Definition] enable_turbine_serial`). Probed once per page; any
 *  failure reads as disabled so the lists fall back to the Duration column. */
export function getEnableTurbineSerial(): Promise<boolean> {
  if (!enabledPromise) {
    enabledPromise = (async () => {
      try {
        if (isTauri()) {
          const { invoke } = await import("@tauri-apps/api/core");
          return Boolean(await invoke<boolean>("get_enable_turbine_serial"));
        }
        if (typeof fetch !== "function") return false;
        const r = await fetch("/api/get_enable_turbine_serial");
        if (!r.ok) return false;
        return Boolean(await r.json());
      } catch {
        return false;
      }
    })();
  }
  return enabledPromise;
}

/** Test hook: forget the cached enable probe. */
export function __resetTurbineSerialForTest(): void {
  enabledPromise = null;
}

/** The backend's cached serial list for `configName` (empty until an event
 *  list has been fetched for that turbine, or when the site has none). */
export async function fetchTurbineSerials(configName: string): Promise<TurbineSerialEntry[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return (await invoke<TurbineSerialEntry[]>("get_turbine_serials", { configName })) ?? [];
    }
    if (typeof fetch !== "function") return [];
    const r = await fetch(`/api/get_turbine_serials?config_name=${encodeURIComponent(configName)}`);
    if (!r.ok) return [];
    return ((await r.json()) as TurbineSerialEntry[]) ?? [];
  } catch {
    return [];
  }
}

/** Serial for the calendar day (UTC, like the rest of the Tiger-seconds UI)
 *  containing `tigerSecs`; "" when no entry matches — the C++
 *  get_ui_turbine_serial_model exact-date match. */
export function serialForTigerSecs(entries: TurbineSerialEntry[], tigerSecs: number): string {
  if (entries.length === 0 || !Number.isFinite(tigerSecs)) return "";
  const d = tigerSecondsToDate(tigerSecs);
  const day = d.getUTCDate();
  const month = d.getUTCMonth() + 1;
  const year = d.getUTCFullYear();
  const hit = entries.find((e) => e.day === day && e.month === month && e.year === year);
  return hit ? hit.serial : "";
}

/** Serial-column support for an event-time list. `enabled` says whether the
 *  list should show Serial in place of Duration (the C++ dialogs zero the
 *  Duration column width and widen Serial when enable_turbine_serial is on);
 *  `serialFor` maps a row's start time to its serial. `refreshKey` should be
 *  the fetched event array so the serials are re-read once the reply that
 *  carried them has been processed. */
export function useTurbineSerials(
  configName: string,
  refreshKey: unknown,
): { enabled: boolean; serialFor: (tigerSecs: number) => string } {
  const [enabled, setEnabled] = useState(false);
  const [entries, setEntries] = useState<TurbineSerialEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    void getEnableTurbineSerial().then((e) => {
      if (!cancelled) setEnabled(e);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !configName) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    void fetchTurbineSerials(configName).then((list) => {
      if (!cancelled) setEntries(list);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, configName, refreshKey]);

  const serialFor = useCallback((tigerSecs: number) => serialForTigerSecs(entries, tigerSecs), [entries]);
  return { enabled, serialFor };
}
